import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

export const snapshotLabels = ['Vorläufig Versandfertig', 'Vorläufige Versandfertigkeit', 'Estimated dispatch readiness', 'Předpokládaná připravenost k odeslání', 'Versandfertigkeit bei Bestellung', 'Dispatch readiness at order', 'Dispatch status when ordered', 'Připravenost k odeslání při objednání'];

export function snapshotDay(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Vienna', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  return ['year', 'month', 'day'].map((type) => parts.find((part) => part.type === type).value).join('-');
}

export function snapshotProperties(existing, variant, config, today = snapshotDay()) {
  const properties = { ...(Array.isArray(existing) ? Object.fromEntries(existing) : existing) };
  for (const key of [...snapshotLabels, '_seal_dispatch_v', '_seal_dispatch_state', '_seal_dispatch_date']) delete properties[key];
  if (!variant.physical) return properties;
  const future = /^\d{4}-\d{2}-\d{2}$/.test(variant.date) && variant.date > today;
  if (!future) return properties;
  const formatted = variant.date.split('-').reverse().join('.');
  return { ...properties, _seal_dispatch_v: '1', _seal_dispatch_state: 'future', _seal_dispatch_date: variant.date,
    [config.label]: config.future.replace('__date__', formatted) };
}

export function sameProperties(left, right) {
  const clean = (value) => (Array.isArray(value) ? value : Object.entries(value ?? {})).filter(([, item]) => item !== '' && item != null).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(clean(left)) === JSON.stringify(clean(right));
}

let queue = Promise.resolve();
let busy = 0;
let dirty = true;
const locked = new Map();
function updateLocks() {
  for (const node of document.querySelectorAll('cart-items-component, .additional-checkout-buttons')) {
    const shouldLock = busy || (dirty && node.matches('.additional-checkout-buttons'));
    if (shouldLock) {
      if (!locked.has(node)) locked.set(node, node.inert);
      if (!node.inert) node.inert = true;
    }
    if (!shouldLock && locked.has(node)) { node.inert = locked.get(node); locked.delete(node); }
  }
  if (!busy && !dirty) {
    for (const [node, inert] of locked) node.inert = inert;
    locked.clear();
  }
}
const config = () => JSON.parse(document.getElementById('seal-dispatch-snapshot-config').textContent);
const cartUrl = () => Theme.routes.cart_url;
const enqueue = (task) => {
  busy++;
  updateLocks();
  const pending = queue.catch(() => {}).then(task);
  queue = pending.finally(() => { busy--; updateLocks(); });
  queue.catch(() => {});
  return pending;
};

async function readSnapshot() {
  const url = new URL(cartUrl(), window.location.origin);
  url.searchParams.set('section_id', 'seal-dispatch-snapshot');
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error('Dispatch data unavailable');
  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  const data = html.querySelector('[data-seal-dispatch-snapshot]');
  if (!data) throw new Error('Dispatch data missing');
  return JSON.parse(data.textContent);
}

async function synchronize() {
  dirty = true;
  const snapshot = await readSnapshot();
  const variants = new Map(snapshot.items.map((item) => [String(item.variant_id), item]));
  let items = snapshot.items;
  let changed = false;
  for (let attempt = 0; attempt <= snapshot.items.length + 1; attempt++) {
    if (items.some((line) => !variants.has(String(line.variant_id)))) throw new Error('Cart changed in another tab');
    const item = items.find((line) => {
      const variant = variants.get(String(line.variant_id));
      return variant && !sameProperties(line.properties, snapshotProperties(line.properties, variant, snapshot.config));
    });
    if (!item) {
      dirty = false;
      return { changed, items, config: snapshot.config, maxDate: snapshot.items.filter((line) => line.physical && line.date > snapshotDay()).map((line) => line.date).sort().at(-1) ?? '' };
    }
    const properties = snapshotProperties(item.properties, variants.get(String(item.variant_id)), snapshot.config);
    const previousProperties = Array.isArray(item.properties) ? Object.fromEntries(item.properties) : item.properties ?? {};
    const payloadProperties = { ...properties };
    for (const key of Object.keys(previousProperties)) {
      if (!(key in properties)) payloadProperties[key] = '';
    }
    const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: payloadProperties }),
    });
    if (!response.ok) throw new Error('Dispatch record could not be saved');
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
    items = cart.items;
    changed = true;
  }
  throw new Error('Cart changed during dispatch update');
}

async function renderedSections(ids, pathname = window.location.pathname) {
  const url = new URL(pathname, window.location.origin);
  url.searchParams.set('sections', ids.join(','));
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error('Cart sections unavailable');
  const sections = await response.json();
  if (ids.some((id) => !sections[id])) throw new Error('Cart section missing');
  return sections;
}

export function dispatchCartFetch(url, options) {
  return enqueue(async () => {
    const response = await fetch(url, options);
    if (!response.ok) return response;
    const data = await response.clone().json();
    if (data.status || data.errors) return response;
    try {
      const { changed } = await synchronize();
      if (!changed) return response;
      const ids = Object.keys(data.sections ?? {});
      if (ids.length) data.sections = await renderedSections(ids);
      if (/\/change(?:\.js)?(?:\?|$)/.test(String(url))) {
        const current = await fetch(`${cartUrl()}.js`, { cache: 'no-store' }).then((result) => result.json());
        Object.assign(data, current);
      }
      return new Response(JSON.stringify(data), { status: response.status, headers: { 'Content-Type': 'application/json' } });
    } catch (error) {
      dirty = true;
      console.error('Dispatch record will be retried before checkout', error);
      return response;
    }
  });
}

class SealDispatchProperties extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.shopify-section, dialog, product-card');
    this.scope?.addEventListener(ThemeEvents.variantUpdate, this.onVariant);
    this.closest('form')?.addEventListener('change', this.update);
    this.update();
  }
  disconnectedCallback() {
    this.scope?.removeEventListener(ThemeEvents.variantUpdate, this.onVariant);
    this.closest('form')?.removeEventListener('change', this.update);
  }
  onVariant = (event) => {
    const productId = event.detail.data.newProduct?.id ?? this.dataset.productId;
    const incoming = Array.from(event.detail.data.html.querySelectorAll('seal-dispatch-properties')).find((node) => node.dataset.productId === String(productId));
    if (incoming) this.dataset.productId = incoming.dataset.productId;
    if (incoming) this.querySelector('script').textContent = incoming.querySelector('script').textContent;
    queueMicrotask(this.update);
  };
  update = () => {
    const data = JSON.parse(this.querySelector('script').textContent);
    const variantId = this.closest('form')?.querySelector('[name="id"]')?.value;
    const variant = data.variants.find((item) => String(item.variant_id) === variantId);
    if (!variant) return;
    this.querySelectorAll('input').forEach((input) => input.remove());
    for (const [key, value] of Object.entries(snapshotProperties({}, variant, data.config))) {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = `properties[${key}]`; input.value = value;
      this.append(input);
    }
  };
}

if (!customElements.get('seal-dispatch-properties')) customElements.define('seal-dispatch-properties', SealDispatchProperties);

let checkoutInProgress = false;
async function prepareCheckout(proceed) {
  if (checkoutInProgress) return;
  checkoutInProgress = true;
  try {
    const visibleDate = displayedDate();
    const result = await enqueue(async () => {
      const result = await synchronize();
      if (result.changed || result.maxDate !== visibleDate) await refreshSections(result.items);
      return result;
    });
    if (result.maxDate !== visibleDate) {
      window.alert(result.config.updated);
      return;
    }
    proceed();
  } catch (error) {
    dirty = true;
    updateLocks();
    console.error(error);
    window.alert(config().error);
  } finally {
    checkoutInProgress = false;
  }
}

document.addEventListener('submit', (event) => {
  if (event.submitter?.name !== 'checkout') return;
  event.preventDefault(); event.stopImmediatePropagation();
  const form = event.target;
  prepareCheckout(() => {
    const currentForm = document.getElementById(form.id) ?? form;
    if (!currentForm.isConnected) throw new Error('Cart form replaced');
    const input = document.createElement('input'); input.type = 'hidden'; input.name = 'checkout'; input.value = 'checkout';
    currentForm.append(input);
    HTMLFormElement.prototype.submit.call(currentForm);
  });
}, true);

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const link = target?.closest('a[href]');
  if (link && new URL(link.href).pathname.match(/\/checkout\/?$/)) {
    event.preventDefault(); event.stopImmediatePropagation();
    prepareCheckout(() => { window.location.href = link.href; });
  }
  const wallet = target?.closest('shopify-accelerated-checkout-cart, .additional-checkout-buttons');
  if (wallet && (busy || dirty)) { event.preventDefault(); event.stopImmediatePropagation(); refreshCartRecord(); }
}, true);

const refreshForms = () => document.querySelectorAll('seal-dispatch-properties').forEach((element) => element.update());
window.addEventListener('pageshow', refreshForms);
document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshForms(); });
window.setInterval(() => { if (!document.hidden) refreshForms(); }, 60000);

async function refreshCartRecord() {
  try {
    const visibleDate = displayedDate();
    await enqueue(async () => {
      const result = await synchronize();
      if (result.changed || result.maxDate !== visibleDate) await refreshSections(result.items);
    });
  } catch (error) {
    dirty = true;
    updateLocks();
    console.error('Dispatch record refresh deferred', error);
  }
}
function displayedDate() {
  const status = document.querySelector('seal-dispatch-status[data-dispatch-context="cart"]');
  return status?.dataset.dispatchDate > snapshotDay() ? status.dataset.dispatchDate : '';
}
async function refreshSections(items) {
  const ids = [...new Set([...document.querySelectorAll('cart-items-component')].map((node) => node.dataset.sectionId).filter(Boolean))];
  const sections = ids.length ? await renderedSections(ids) : {};
  await Promise.all(ids.filter((id) => sections[id]).map((id) => morphSection(id, sections[id], { mode: document.querySelector(`cart-items-component[data-section-id="${id}"][data-drawer]`) ? 'hydration' : 'full', injectStylesheet: true })));
  document.dispatchEvent(new CartUpdateEvent({ items, item_count: items.reduce((sum, item) => sum + item.quantity, 0) }, 'seal-dispatch-snapshot', { source: 'seal-dispatch-snapshot', itemCount: items.reduce((sum, item) => sum + item.quantity, 0), sections }));
}
new MutationObserver(() => { if (busy || dirty) updateLocks(); }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['inert'] });
window.addEventListener('pageshow', refreshCartRecord);
document.addEventListener('dialog:open', () => { if (!busy) refreshCartRecord(); }, true);
document.addEventListener('visibilitychange', () => { if (!document.hidden && !busy) refreshCartRecord(); });
window.setInterval(() => { if (!document.hidden && !busy) refreshCartRecord(); }, 60000);
